import {
	SubjectTrackStatus,
	TrackHopDecider,
	TrackHopState,
} from "@sitescene/types";

type BadgeTone = "brand" | "error" | "gray" | "success" | "warning";

/** What a track's status means to someone watching it. */
export const TRACK_STATUS: Record<
	SubjectTrackStatus,
	{ label: string; tone: BadgeTone }
> = {
	[SubjectTrackStatus.AwaitingSeed]: {
		label: "Pick the person",
		tone: "warning",
	},
	[SubjectTrackStatus.Complete]: { label: "Done", tone: "success" },
	[SubjectTrackStatus.Dismissed]: { label: "Dismissed", tone: "gray" },
	[SubjectTrackStatus.Expanding]: { label: "Following", tone: "brand" },
	[SubjectTrackStatus.Failed]: { label: "Failed", tone: "error" },
	[SubjectTrackStatus.Indexing]: { label: "Checking clips", tone: "brand" },
	[SubjectTrackStatus.NoSubject]: { label: "Nobody to follow", tone: "gray" },
};

export const HOP_STATE: Record<
	TrackHopState,
	{ label: string; tone: BadgeTone }
> = {
	[TrackHopState.Auto]: { label: "Auto", tone: "gray" },
	[TrackHopState.Confirmed]: { label: "Confirmed", tone: "success" },
	[TrackHopState.NeedsReview]: { label: "Needs review", tone: "warning" },
	[TrackHopState.Rejected]: { label: "Rejected", tone: "error" },
};

/** Who settled a hop that went to review. */
export const DECIDED_BY: Record<TrackHopDecider, string> = {
	[TrackHopDecider.Operator]: "by staff",
	[TrackHopDecider.Verifier]: "by Gemini",
};

/** Wall-clock time with seconds, e.g. "9:42:07 AM". */
export const formatClock = (value: Date | string): string =>
	new Date(value).toLocaleTimeString(undefined, {
		hour: "numeric",
		minute: "2-digit",
		second: "2-digit",
	});

/** A span of seconds in words: "45 s", "3 min 05 s". */
export const formatSpan = (seconds: number): string => {
	const whole = Math.max(0, Math.round(seconds));
	if (whole < 60) return `${whole} s`;
	const minutes = Math.floor(whole / 60);
	return `${minutes} min ${String(whole % 60).padStart(2, "0")} s`;
};

export const secondsBetween = (from: Date | string, to: Date | string) =>
	(new Date(to).getTime() - new Date(from).getTime()) / 1000;

/** A 0–1 score to two decimals. */
export const formatScore = (value: number): string => value.toFixed(2);
